import { z } from "zod/v3";
import { validateUrl } from "./utils";
import { isRichTextContentEmpty } from "@/lib/htmlHelpers";

const DateSchema = z.object({
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required")
});
// Helper for fields that can be either string or null
const nullableString = z.union([z.string(), z.null()]).transform((val) => (val === null ? "" : val));

// NOTE: We will create `JobPostSchemaPartial` *after* `JobPostSchema` is declared to avoid forward
// reference issues.

// Core field definitions (without additional `refine` logic) so that we can build different
// variants (full, partial) off the same base.
const JobPostObject = z.object({
  name: z.string().min(1, "Job Title is required"),
  facilityId: z.string().min(1, "Facility is required"),
  // Overview is optional at draft time, but if provided it must contain actual content
  overview: z
    .string()
    .optional()
    .refine((val) => val === undefined || !isRichTextContentEmpty(val), {
      message: "Overview is required"
    }),
  stage: z.enum(["Draft", "Pending", "In Review", "Assist", "Active", "Inactive", "Unposted"]),
  serviceLevel: z.enum(["Self-Serve", "Assisted", "Advanced", "Executive"]),
  compensation: z
    .object({
      baseMaximum: z.number().optional(),
      baseMinimum: z.number().optional(),
      totalAnticipated: z.number().optional(),
      currencyUnit: z.literal("USD")
    })
    .superRefine((data, ctx) => {
      if (data.baseMaximum !== undefined && data.baseMinimum !== undefined) {
        if (data.baseMaximum < data.baseMinimum) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Base Maximum must be greater than Base Minimum",
            path: ["baseMaximum"]
          });
        }
      }
    }),
  benefits: nullableString.optional(),
  additionalIncome: nullableString.optional(),
  employerId: z.string().optional(),
  employerData: z.any(),
  pointOfContact: z.object({
    name: z.string().min(1, "Contact name is required"),
    email: z.string().min(1, "Contact email is required").email("Invalid email format"),
    title: z.string().optional(),
    phone: z.string().optional()
  }),
  category: z.string().min(1, "Category is required"),
  requiredEducation: z.string().min(1, "Required Education is required"),
  requiredExperienceInYears: z.string().min(1, "Required Experience in Years is required"),
  requiredExperienceDescription: nullableString.optional(),
  hideCompensationFromJobBoard: z.boolean(),
  essentialResponsibilities: nullableString.optional(),
  applicantGroup: z.string().min(1, "Applicant Group is required"),
  applyVia: z.enum(["PGA.org / Email Notification", "Facility Website"]),
  paySchedule: z.enum(["Hourly", "Salary", "Weekly"]),
  positionType: z.string().min(1, "Position Type is required"),
  otherSections: z.array(z.string()).optional(),
  otherStates: z.array(z.string()).optional(),
  submissionDeadline: DateSchema,
  additionalPgaEducation: nullableString.optional(),
  externalJobUrl: nullableString.optional().refine((val) => (val ? validateUrl(val) === true : true), {
    message: "Application URL is not valid"
  })
});

// Full schema with additional cross-field validation
export const JobPostSchema = JobPostObject.refine(
  (data) =>
    data.serviceLevel === "Assisted" && data.applyVia === "Facility Website"
      ? data.externalJobUrl !== null && data.externalJobUrl !== ""
      : true,
  {
    message: "Must include URL when Facility Website is chosen.",
    path: ["externalJobUrl"]
  }
);

// NEW: Create a version of the full JobPost schema where **all** properties are optional.
// This allows us to reuse the detailed field-level validation defined above while only
// requiring the subset relevant for a particular workflow (e.g. save-as-draft).
export const JobPostSchemaPartial = JobPostObject.partial();

// Assisted – Save Draft
export const AssistedJobPostDraftSchema = JobPostSchemaPartial.merge(
  z
    .object({
      // core required fields
      // Service level must be provided, but any valid option is allowed at draft time.
      serviceLevel: z.enum(["Self-Serve", "Assisted", "Advanced", "Executive"]),
      category: z.string().min(1, "Category is required"),
      name: z.string().min(1, "Job Title is required"),
      positionType: z.string().min(1, "Position Type is required"),
      facilityId: z.string().min(1, "Facility is required"),
      employerId: z.string().optional(),
      pointOfContact: z.object({
        name: z.string().min(1, "Contact name is required"),
        email: z.string().min(1, "Contact email is required").email("Invalid email format"),
        title: z.string().optional(),
        phone: z.string().optional()
      })
    })
    // allow any additional keys without validation (they will be ignored)
    .passthrough()
);

// Assisted – Publish (all fields except the NOT-REQUIRED list)
export const AssistedJobPostPublishSchema = AssistedJobPostDraftSchema.merge(
  z.object({
    employerId: z.string().min(1, "Employer is required"),
    // Additional fields required for publish
    overview: z.string().refine((val) => !isRichTextContentEmpty(val), {
      message: "Overview is required"
    }),
    applicantGroup: z.string().min(1, "Applicant Group is required"),
    requiredEducation: z.string().min(1, "Required Education is required"),
    requiredExperienceInYears: z.string().optional(),

    submissionDeadline: DateSchema.refine((val) => val && val.startDate && val.endDate, {
      message: "Submission deadline is required"
    }),

    // Compensation is required for publish
    compensation: z
      .object({
        baseMaximum: z.number().min(1, "Base Maximum is required"),
        baseMinimum: z.number().min(1, "Base Minimum is required"),
        totalAnticipated: z.number().min(1, "Total Compensation is required"),
        currencyUnit: z.literal("USD")
      })
      .superRefine((data, ctx) => {
        if (data.baseMaximum < data.baseMinimum) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Base Maximum must be greater than Base Minimum",
            path: ["baseMaximum"]
          });
        }
      })
  })
).passthrough();

export const SelfServeJobPostDraftSchema = AssistedJobPostDraftSchema.omit({
  serviceLevel: true
})
  .extend({
    serviceLevel: z.enum(["Self-Serve", "Assisted", "Advanced", "Executive"]).optional()
  })
  .passthrough();

export const SelfServeJobPostSchema = AssistedJobPostPublishSchema.omit({
  serviceLevel: true,
  employerId: true
})
  .extend({
    serviceLevel: z.enum(["Self-Serve", "Assisted", "Advanced", "Executive"]).optional(),
    employerId: z.string().optional()
  })
  .passthrough();
